<template>
  <v-card class="mx-auto" outlined>
    <v-card-title class="d-flex justify-end pr-1 pb-0 mb-0" v-if="!isNew()">
      <v-menu bottom left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn x-small text v-bind="attrs" v-on="on" class="">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list flat>
          <v-list-item @click="onEdit()">
            <v-icon class="mr-2">mdi-pencil</v-icon>
            <v-list-item-content>Edit</v-list-item-content>
          </v-list-item>
          <v-list-item @click="onDelete()">
            <v-icon class="mr-2">mdi-delete</v-icon>
            <v-list-item-content>Delete</v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>   
    </v-card-title>
    <v-card-text>
      <v-container class="py-0">
        <v-row>
          <v-col cols="6">
            <v-text-field 
              v-model="name" 
              label="Name"
              :disabled="isViewOnly()">
            </v-text-field>
          </v-col>
        </v-row>
        <v-row v-for="entry in entries" :key="entry.uuid">
          <v-col cols="8">
            <v-select
              v-model="entry.account"
              :items="accounts"
              item-text="fname"
              label="Account"
              return-object
              required
              :disabled="isViewOnly()"
            ></v-select>
          </v-col>
          <v-col cols="3">
            <number-field
              label="Percentage"
              :value="entry.percentage"
              :minValue="-100"
              :maxValue="100"
              @onNumber="entry.percentage = $event"
              :disabled="isViewOnly()"
            />
          </v-col>
          <v-col class="d-flex ml-n6" cols="1" v-if="showMoreEntry">
            <v-btn
              class="align-self-center"
              text
              x-small
              @click="onDeleteEntry(entry)"
            >
              <v-icon>mdi-table-row-remove</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="editMode">
          <v-col cols="4">
            <v-btn text x-small @click="onMoreEntry">
              <v-icon>mdi-table-row-plus-after</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="!isViewOnly()">
            <v-btn color="primary" @click="onSave()">Save</v-btn>
            <v-btn class="ml-3" @click="onCancel()">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-container>
      <confirmation-dialog 
        dialogType="info"
        :alertType="infoDialog.type"
        :title="infoDialog.title"
        :message="infoDialog.message"
        :show="infoDialog.show"
        @onOk="infoDialog.onOk"/>      
    </v-card-text>
  </v-card>
</template>

<script>
import NumberField from "@/components/common/NumberField"
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import { Account, uuid } from "@/model";


function emptyEntry() {
  return { uuid: uuid(), account: undefined, percentage: 0 };
}

function isValidEntry(entry) {
  return entry.uuid && entry.account;
}

function copyEntries(entries) {
  return [...entries.map(ent => Object.assign({}, ent))];
}

const VIEW_EDIT = "viewEdit";
const NEW = "new";

export default {
  components: {
    NumberField,
    ConfirmationDialog
  },
  props: {
    data: {
      type: Object,
      default: () => {}
    },
    mode: {
      type: String,
      default: "viewEdit"
    }
  },
  async created() {
    await this.$store.dispatch("getAccounts");
    this.entries = this.data ? copyEntries(this.data.entries) : [emptyEntry(), emptyEntry()];
    this.name = this.data ? this.data.name : "";
    this.uuid = this.data ? this.data.uuid : undefined;
    this.editMode = (this.mode === NEW);
  },
  data: () => ({
    entries: [],
    uuid: undefined,
    name: undefined,
    editMode : false,
    infoDialog: {type: "info", title: "", message: "", show: false, onOk: () => {} },
  }),
  computed: {
    accounts() {
      return Account.sort(this.$store.state.accounts)
    },
    showMoreEntry() {
      return (this.entries.length > 2) && (this.editMode);
    },
    isEditable() {
      return this.mode === VIEW_EDIT;
    }
  },
  methods: {
    isViewOnly() {
      return (this.mode !== NEW) && !this.editMode;
    },
    isNew() {
      return this.mode === NEW;
    },
    onMoreEntry() {
      this.entries.push(emptyEntry());
    },
    onDeleteEntry(entry) {
      this.entries = this.entries.filter((e) => e.uuid !== entry.uuid);
    },
    onEdit() {
      this.editMode = true;
    },
    showInfo(message, type) {
      this.infoDialog = {
        type: type ? type : "info", 
        title: "", 
        message: message, 
        show: true, 
        onOk: () => { this.infoDialog.show = false; } 
      }
    },
    validate() {
      if (!this.name) {
        this.showInfo("Name is empty", "error");
        return false;
      }

      const invalidEntries = this.entries
        .map((entry, i) => {
          return [i, isValidEntry(entry)];
        })
        .filter(tup => !tup[1]);

      if (invalidEntries.length > 0) {
        const indexes = invalidEntries.map(tup => (tup[0] + 1)).join(",");
        let msg = (indexes.length > 1) ? "Entries " : "Entry ";
        msg += indexes + " not valid";
        this.showInfo(msg, "error");
        return false;
      }

      const sum = this.entries
        .map(entry => Math.abs(entry.percentage))
        .reduce((a,b) => a + b, 0);
      
      if (sum != 200) {
        this.showInfo("Percentage does not sum up to 100", "error");
        return false;
      }
      return true;
    },
    getValue() {
      return {
        uuid: this.uuid,
        name: this.name,
        entries: this.entries
      }
    },
    onDelete() {
      this.$emit("onDelete", this.getValue());
    },
    onSave() {
      if (!this.validate()) {
        return;
      }
      this.$emit("onSave", this.getValue());
    },
    onCancel() {
      this.entries = this.data ? copyEntries(this.data.entries) : [emptyEntry(), emptyEntry()];
      this.name = this.data ? this.data.name : "";
      this.editMode = false;
    }
  }
};
</script>

<style lang="scss" scoped>
</style>